import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { defaultPath } from "@/components/ProtectedRoute";
import { formatApiError } from "@/lib/api";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showForgot, setShowForgot] = useState(false);

  if (user && user.role) return <Navigate to={defaultPath(user.role)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email.trim(), password);
      nav(defaultPath(u.role));
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: "100vh", display: "grid", gridTemplateColumns: "1fr 1fr", background: "#0A0A0A" }} className="max-md:!grid-cols-1">
      {/* LEFT: brand panel */}
      <div style={{ padding: "60px 56px", display: "flex", flexDirection: "column", justifyContent: "space-between", position: "relative", overflow: "hidden" }} className="max-md:!hidden">
        <Brand size="md" />
        <div style={{ zIndex: 2 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "#BF3437", fontWeight: 700, marginBottom: 16, textTransform: "uppercase" }}>
            Plataforma Operativa
          </div>
          <h1 style={{ color: "#FFFBF4", fontSize: 56, fontWeight: 900, lineHeight: 1.05, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            Cada ronda.<br/>
            Cada novedad.<br/>
            <span style={{ color: "#BF3437" }}>Demostrable.</span>
          </h1>
          <p style={{ color: "rgba(255,251,244,0.6)", fontSize: 14, maxWidth: 420, lineHeight: 1.6 }}>
            Centralizá supervisión, control de rondas con QR + GPS, novedades por cliente y reportes auditables.
            Lo que se hace, queda probado.
          </p>
        </div>
        <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "rgba(255,251,244,0.4)", textTransform: "uppercase" }}>
          INVICT Security Solutions · 2026
        </div>
        {/* decorative red bar */}
        <div style={{ position: "absolute", right: -120, top: 80, width: 280, height: 280, background: "radial-gradient(circle, rgba(191,52,55,0.18), transparent 70%)" }} />
        <div style={{ position: "absolute", right: 40, bottom: 40, width: 4, height: 120, background: "#BF3437" }} />
      </div>

      {/* RIGHT: form */}
      <div style={{ background: "#FFFBF4", padding: "60px 56px", display: "flex", flexDirection: "column", justifyContent: "center" }} className="max-md:!p-6">
        <div style={{ maxWidth: 380, width: "100%", marginInline: "auto" }}>
          <div className="md:hidden" style={{ marginBottom: 32 }}>
            <Brand size="md" invert />
          </div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
            Acceso seguro
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.1, color: "#0A0A0A", margin: "0 0 32px", letterSpacing: "-0.02em" }}>
            Ingresá a la operación
          </h2>

          <form onSubmit={onSubmit} data-testid="login-form">
            <div className="field">
              <label>Email</label>
              <div style={{ position: "relative" }}>
                <Mail size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#6B655A" }} />
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="tu@invict.com" required style={{ paddingLeft: 40 }} autoFocus data-testid="login-email" />
              </div>
            </div>
            <div className="field">
              <label>Contraseña</label>
              <div style={{ position: "relative" }}>
                <Lock size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#6B655A" }} />
                <input value={password} onChange={(e) => setPassword(e.target.value)} type={show ? "text" : "password"} placeholder="••••••••" required style={{ paddingLeft: 40, paddingRight: 40 }} data-testid="login-password" />
                <button type="button" onClick={() => setShow(!show)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#6B655A", cursor: "pointer" }} data-testid="login-toggle-show">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {err && (
              <div style={{ background: "rgba(191,52,55,0.08)", border: "1px solid rgba(191,52,55,0.3)", color: "#8E2629", padding: "10px 12px", fontSize: 13, marginBottom: 12, borderRadius: 2 }} data-testid="login-error">
                {err}
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: "14px", marginTop: 8, fontSize: 13 }} data-testid="login-submit">
              {busy ? "Ingresando..." : "Ingresar"}
            </button>

            <button type="button" onClick={() => setShowForgot(true)} style={{ background: "none", border: "none", color: "#6B655A", fontSize: 12, fontWeight: 600, marginTop: 18, cursor: "pointer", textDecoration: "underline" }} data-testid="login-forgot">
              ¿Olvidaste tu contraseña?
            </button>
          </form>
        </div>
      </div>

      {showForgot && (
        <div className="modal-overlay" onClick={() => setShowForgot(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Recuperar contraseña</h3>
            <p>Para recuperar tu contraseña, contactá a un administrador y te genera una nueva al instante. Por seguridad, no enviamos emails automáticos.</p>
            <button className="btn-primary" onClick={() => setShowForgot(false)} data-testid="forgot-close">Entendido</button>
          </div>
        </div>
      )}

      {/* Marca de build para verificar si el deploy tomó el código nuevo del frontend.
          Visible sin login. Único e inconfundible: si NO aparece este texto exacto en
          producción tras un redeploy, el bundle desplegado no se reconstruyó. */}
      <div data-testid="build-marker" style={{
        position: "fixed", bottom: 6, right: 8, zIndex: 999,
        fontSize: 9, letterSpacing: "0.08em", color: "rgba(150,150,150,0.55)",
        fontFamily: "monospace", pointerEvents: "none",
      }}>
        BUILD-CALLSOS-20260721-01
      </div>
    </div>
  );
}
